import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellComponent,
  ColumnDefinition,
  RowComponent,
} from "tabulator-tables";
import { Users } from "../../API/Users";
import AccountFilter from "../../components/AccountFilter";
import ReusableModal from "../../components/ReusableModal";
import { FilterType, useFilter } from "../../customHooks/useFIlters";
import { useManageAccount } from "../../customHooks/useManageAccount";
import { TDate } from "../../TDate";

import { useNavigate, useSearchParams } from "react-router-dom";
import { Preferences } from "../../API/Preferences";
import { Publications } from "../../API/Publications";
import { deepClone } from "../../API/utils/deepClone";
import { httpAll } from "../../API/utils/httpAll";
import {
  TableParamsV2,
  TableV2,
  TrendratingTableV2,
} from "../../components/TableV2";
import { useFeedback } from "../../customHooks/useFeedback";
import ManageAccountModalContent from "./ManageAccountModalContent";
import {
  getProductCode,
  prepareDailyReportProp,
  userPropsToFetch,
  userTypes,
} from "./utils";
// import { set } from "date-fns";

const printActive = (cell: any, formatterParams: any, onRendered: any) => {
  const isActive = cell.getValue();
  const color = isActive ? "green" : "red";
  return `<div style='width:100%; display:flex;justify-content:center; align-items:center; height:100%'><span style='background-color:${color}; height:10px; width:10px;border-radius:100px; display:block;'></span></div>`;
};

const printEmailWithAccounIdOnHover = (cell: any) => {
  const rowValue = cell.getRow().getData();
  return `<span title="${rowValue.email} - Account ID: ${rowValue.id}">${rowValue.email}</span>`;
};

export type validationFields = {
  firstName: boolean;
  lastName: boolean;
  group: boolean;
  email: boolean;
  level: boolean;
  creationTime: boolean;
  updateDate: boolean;
  owner: boolean;
  productId: boolean;
  dailyReport: boolean;
};

const formatPermisisons = (cell: CellComponent) => {
  const data = cell.getData();

  return data?.["permissions"]
    .map((perm: any) => {
      let str = perm.permission.toLowerCase();
      let result = str.charAt(0).toUpperCase() + str.slice(1);

      return result;
    })
    .sort((a: string, b: string) =>
      a.toLowerCase() > b.toLowerCase() ? 1 : -1
    )
    .join(", ");
};

const sortPermissions = (
  a: any,
  b: any,
  aRow: any,
  bRow: any,
  column: any,
  dir: "asc" | "desc"
) => {
  if (a.length === 0) {
    return dir === "desc" ? -1 : 1;
  }

  if (b.length === 0) {
    return dir === "desc" ? 1 : -1;
  }

  a = a
    .map((perm: any) => {
      let str = perm.permission.toLowerCase();
      let result = str.charAt(0).toUpperCase() + str.slice(1);

      return result;
    })
    .sort((a: string, b: string) =>
      a.toLowerCase() > b.toLowerCase() ? 1 : -1
    )
    .join(", ");

  b = b
    .map((perm: any) => {
      let str = perm.permission.toLowerCase();
      let result = str.charAt(0).toUpperCase() + str.slice(1);

      return result;
    })
    .sort((a: string, b: string) =>
      a.toLowerCase() > b.toLowerCase() ? 1 : -1
    )
    .join(", ");

  if (a > b) {
    return 1;
  } else if (a < b) {
    return -1;
  }

  return 0;
};

const Accounts = memo(function Acc() {
  const preferencesAPI = useMemo(() => new Preferences(), []);
  const userAPI = useMemo(() => new Users(), []);
  const publicationAPI = useMemo(() => new Publications(), []);
  const { getFilter, resetFilter, setFilter, getDefault } = useFilter();
  const { retrieveSubscriptionsForSaving, setSubscriptions } =
    useManageAccount();
  const feedback = useFeedback();
  const manageAccountModalContentRef = useRef<any>(null);
  const acocuntFilterRef = useRef<any>(null);

  const [urlParams] = useSearchParams();

  //#region - TABLE DATA & COLUMNS
  const [coutRows, setCoutRows] = useState(0);
  const [isTableDataLoading, setIsTableDataLoading] = useState(false);
  const [sellers, setSellers] = useState<any[]>([]);
  const [tableReady, setTableReady] = useState(false);

  const tableRef = useRef<{ table: TableV2 }>();
  const hiddenButtonRef = useRef<HTMLButtonElement>(null);
  const getSellers = useCallback(async () => {
    // let owners = await userAPI.getOwners();
    // owners = owners.data.owners;
    // setSellers(owners);

    const allowedToBeOwners: any[] = [];
    let users = await userAPI.getForSearch([
      {
        dimension: "level",
        segments: ["INTERNAL"],
      },
    ]);
    users = users.data.ids;
    users = await userAPI.fetchUserProperties(
      ["permissions", "firstName", "lastName"],
      users
    );
    users = users.data.rows;

    const allowedPermissions = ["SELLER"];
    users.forEach((user: any) => {
      let permissions = user.permissions.map((p: any) => p.permission);
      if (permissions.some((p: string) => allowedPermissions.includes(p))) {
        allowedToBeOwners.push(user.lastName + " " + user.firstName);
      }
    });

    setSellers(allowedToBeOwners);
  }, [userAPI]);

  const columns: ColumnDefinition[] = useMemo(
    () => [
      {
        title: "",
        hozAlign: "center",
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        widthGrow: 0.2,
      },
      {
        widthGrow: 0.5,
        title: "Active",
        field: "active",
        hozAlign: "center",
        formatter: printActive,
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Client",
        field: "group",
        hozAlign: "left",
        widthGrow: 2,
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "First name",
        field: "firstName",
        hozAlign: "left",
        widthGrow: 1,
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Last name",
        field: "lastName",
        hozAlign: "left",
        widthGrow: 1,
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Email",
        field: "email",
        hozAlign: "left",
        widthGrow: 2,
        formatter: printEmailWithAccounIdOnHover,
        sorterParams: { alignEmptyValues: "bottom" },
      },

      {
        widthGrow: 1,
        title: "Type",
        field: "level",
        hozAlign: "left",
        formatter: (cell) => {
          let type = cell.getValue();
          type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
          return type;
        },
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Owner",
        field: "owner",
        hozAlign: "left",
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Product",
        field: "productId",
        formatter: (e) => {
          let data: any = e.getData();
          data = data.product.codeName;
          return data;
        },
        hozAlign: "left",
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Permissions",
        field: "permissions",
        formatter: formatPermisisons,
        sorter: sortPermissions,
        hozAlign: "left",
        sorterParams: { alignEmptyValues: "bottom" },
      },
      {
        title: "Created",
        field: "creationTime",
        hozAlign: "center",
        sorterParams: { alignEmptyValues: "bottom" },
        formatter: (cell) => {
          let date = new Date(cell.getValue());
          return TDate.dateToIso8601(date);
        },
      },
      {
        title: "Conversion ",
        field: "levelUpdate",
        hozAlign: "center",
        sorterParams: { alignEmptyValues: "bottom" },
        formatter: (cell) => {
          let date = new Date(cell.getValue());
          return TDate.dateToIso8601(date);
        },
      },
    ],
    []
  );

  const getTableData = useCallback(
    async (f: any) => {
      const filters: any[] = [];
      const {
        userStatusFilter,
        groupFilter,
        ownerFilter,
        levelFilter,
        productFilter,
      } = f as FilterType;
      // if (!levelParam) {
      let _status = userStatusFilter.map((item: any) =>
        item === "true" ? 1 : 0
      );

      if (_status.length > 0) {
        filters.push({
          dimension: "active",
          segments: [..._status],
        });
      }
      if (groupFilter.length > 0) {
        filters.push({
          dimension: "group",
          segments: [...groupFilter],
        });
      }

      if (levelFilter.length > 0) {
        filters.push({
          dimension: "level",
          segments: [...levelFilter],
        });
      }

      if (productFilter.length > 0) {
        filters.push({
          dimension: "product.id",
          segments: [...productFilter],
        });
      }

      if (ownerFilter.length > 0) {
        filters.push({
          dimension: "owner",
          segments: [...ownerFilter],
        });
      }
      // } else {
      //   filters.push({
      //     dimension: "level",
      //     segments: [levelParam],
      //   });
      // }
      // setIsTableDataLoading(true);

      //!---------------------------------------------------
      //!indagare perché non si riesce ad accedere ai sorters
      // const tabulatorInstance = tabulatorRef?.current?.tabulatorInstance();
      // if (tabulatorInstance) {
      //   let sorters = tabulatorInstance?.getSorters();
      //   debugger;
      // }
      //!---------------------------------------------------
      //!---------------------------------------------------

      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        let activeUsersId = await userAPI.getForFiltering(
          filters,
          abortControllerRef.current
        );
        activeUsersId = activeUsersId.data.ids;

        let data = await userAPI.fetchUserProperties(
          userPropsToFetch,
          activeUsersId,
          abortControllerRef.current
        );
        data = data.data.rows;
        //#region - injection of product id in the obj
        data.forEach((element: any) => {
          element.productId = element.product.id;
        });
        //#endregion
        // setTableData(data);
        // setIsTableDataLoading(false);
        abortControllerRef.current = null;
        return data;
      } catch (error) {
        abortControllerRef.current = null;
        return null;
      }
    },
    [userAPI]
  );
  const fetchDataWithDefaultFilter = useCallback(() => {
    const _default = getDefault();
    getTableData(_default).then((data: any) => {
      tableRef.current?.table?.insertData(data);
      setCoutRows(data.length);
      acocuntFilterRef?.current?.refresh();
    });
  }, [getDefault, getTableData]);

  const onSearch = useCallback(
    async (toSearch: string) => {
      const fields = (str: string) => {
        return str.trim().split(/\s+/);
      };

      let temp = fields(toSearch);
      if (temp.length === 0 || (temp.length === 1 && temp[0] === "")) {
        setIsTableDataLoading(true);
        acocuntFilterRef?.current?.refresh();
        fetchDataWithDefaultFilter();
        setIsTableDataLoading(false);

        return;
      }

      let toSearchSplited: any = ["%" + toSearch.trim() + "%"];
      const searches: any[] = [];
      setIsTableDataLoading(true);
      searches.push({
        dimension: "email",
        segments: toSearchSplited,
      });

      searches.push({
        dimension: "firstName",
        segments: toSearchSplited,
      });

      searches.push({
        dimension: "lastName",
        segments: toSearchSplited,
      });

      let activeUsersId = await userAPI.getForSearch(searches);
      activeUsersId = activeUsersId.data.ids;

      let data = await userAPI.fetchUserProperties(
        userPropsToFetch,
        activeUsersId
      );
      data = data.data.rows;
      //#region - injection of product id in the obj
      data.forEach((element: any) => {
        element.productId = element.product.id;
      });
      //#endregion
      // setSearchResult(data);

      if (data && tableReady) {
        tableRef.current?.table?.insertData(data);
        setCoutRows(data.length);
      }
      setIsTableDataLoading(false);
      // acocuntFilterRef?.current?.enableInputSearch();
    },
    [fetchDataWithDefaultFilter, tableReady, userAPI]
  );

  const abortControllerRef = useRef<any>(null); // Referenza per l'AbortController

  const filter = useCallback(async () => {
    if (tableReady) {
      setIsTableDataLoading(true);
      const _filters = deepClone(getFilter());
      const filtered = await getTableData(_filters);
      if (filtered) {
        tableRef.current?.table?.insertData(filtered);
        setCoutRows(filtered.length);
      }
      setIsTableDataLoading(false);
    }
  }, [getFilter, getTableData, tableReady]);

  useEffect(() => {
    if (tableReady) {
      const type = urlParams.get("levelFilter");
      if (type && (type === "PARTNER" || type === "INTERNAL")) {
        setFilter("levelFilter", [type]);
      }

      const _default = getFilter();
      getTableData(_default).then((data: any) => {
        tableRef.current?.table?.insertData(data);
        setCoutRows(data.length);
        acocuntFilterRef?.current?.refresh();
      });
    }
  }, [getFilter, getTableData, urlParams, setFilter, tableReady]);

  useEffect(() => {
    return () => {
      resetFilter();
    };
  }, [resetFilter]);

  //#endregion

  const { getProductOptions, setProductCode, setAccountID } =
    useManageAccount();

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  // const onRowSelect = useCallback((event: any, row: RowComponent) => {
  //   let table = row.getTable();
  //   const rows = table.getSelectedRows();

  //   const _rows: any[] = [];
  //   if (rows.length > 0) {
  //     for (const r of rows) {
  //       const data = r.getData();
  //       _rows.push(data);
  //     }
  //     setSelectedRows(_rows);
  //   } else {
  //     setSelectedRows(_rows);
  //   }
  // }, []);

  const selectOnDBClick = useCallback((event: any, row: RowComponent) => {
    let table = row.getTable();
    const rows = table.getSelectedRows();

    for (const r of rows) {
      r.deselect();
    }

    row.select();

    const data = row.getData();

    setSelectedRows([data]);
  }, []);

  const onImpersonate = useCallback(() => {
    var baseUrl = process.env.REACT_APP_CLIENT_URL;
    var endPoint =
      [
        "impersonate",
        selectedRows[0].id,
        selectedRows[0].firstName,
        selectedRows[0].lastName,
      ].join("/") + "/";
    var url = baseUrl + endPoint;
    window.open(url);
  }, [selectedRows]);

  const navigate = useNavigate();
  const goToAnalytics = useCallback(() => {
    const ids: number[] = selectedRows.map((row) => row.id);
    navigate(`/analytics?accountIds=${ids.join(",")}`);
  }, [navigate, selectedRows]);

  const [showManageAccountModal, setShowManageAccountModal] = useState(false);

  const accountToManage = useMemo(() => {
    if (selectedRows.length === 1) {
      return selectedRows[0];
    } else {
      return null;
    }
  }, [selectedRows]);

  const [initStateForContentModal, setInitStateForContentModal] =
    useState<any>(null);

  const onManage = useCallback(
    async (account: any) => {
      if (account) {
        setInitStateForContentModal(account);
        // dispatch({
        //   type: "SET_PRODUCT_CODE",
        //   value: getProductCode(productOptions, account.productId),
        // });
        setAccountID(account.id);

        const _prodOptions = await getProductOptions(account.level);
        let temp = getProductCode(_prodOptions, account?.productId);
        setProductCode(temp);
      }
      if (sellers.length === 0) {
        getSellers();
      }
      setShowManageAccountModal(true);
    },
    [
      getProductOptions,
      getSellers,
      sellers.length,
      setAccountID,
      setProductCode,
    ]
  );

  const onCreateNewAccount = useCallback(async () => {
    setInitStateForContentModal(null);
    setSelectedRows([]);
    if (sellers.length === 0) {
      getSellers();
    }
    setShowManageAccountModal(true);
  }, [getSellers, sellers.length]);

  //#endregion

  const closeModal = useCallback(() => {
    setShowManageAccountModal(false);
    manageAccountModalContentRef.current.resetValidation();
    setSubscriptions(null);
  }, [setSubscriptions]);

  const subscribe = useCallback(
    async (toSubscribe: {}, accountId: number) => {
      let _toSubscribe: any = deepClone(toSubscribe);
      _toSubscribe["list"] = [
        ..._toSubscribe["baskets"],
        ..._toSubscribe["portfolios"],
      ];
      delete _toSubscribe["baskets"];
      delete _toSubscribe["portfolios"];
      let keys = Object.keys(_toSubscribe);
      keys.forEach((key) => {
        if (_toSubscribe[key].length === 0) {
          delete _toSubscribe[key];
        } else {
          _toSubscribe[key] = _toSubscribe[key].map((id: number) =>
            publicationAPI.subscribe({
              objectId: id,
              userId: accountId,
              type: key as any,
            })
          );
        }
      });
      keys = Object.keys(_toSubscribe);
      if (keys.length > 0) {
        const successCallback = () => {};
        const failCallback = () => {};
        httpAll(_toSubscribe).then(successCallback, failCallback);
      }
    },
    [publicationAPI]
  );

  const onSaveAccountChanges = useCallback(async () => {
    try {
      const dataIsValid = manageAccountModalContentRef.current.validate();
      if (!dataIsValid) {
        manageAccountModalContentRef.current.allowCorrection(true);
        return;
      } else {
        const manageAccountState =
          manageAccountModalContentRef.current.getState();
        manageAccountModalContentRef.current.allowCorrection(false);

        if (manageAccountState.id != null) {
          //EDIT
          let _manageAccountState = prepareDailyReportProp(manageAccountState);
          try {
            //#region - EMAILDIGEST FROM V1 TO V2
            //if the user is from v1 and is going to v2
            // we need to update the emailDigest value in his saved preferences
            if (initStateForContentModal != null) {
              const isFromV1: boolean =
                initStateForContentModal?.emailSubscription === true &&
                initStateForContentModal?.emailSubscriptionV2 === false;
              const toV2 =
                _manageAccountState?.emailSubscription === false &&
                _manageAccountState?.emailSubscriptionV2 === true;

              const isFromV1andV2 =
                initStateForContentModal?.emailSubscription === true &&
                initStateForContentModal?.emailSubscriptionV2 === true;
              if ((isFromV1 && toV2) || isFromV1andV2) {
                const preferenceArr = await preferencesAPI.select(
                  "PREFERENCE_GENERIC",
                  manageAccountState.id
                );
                // if the user has preferences
                if (preferenceArr && preferenceArr.length > 0) {
                  const preferenceId: any = preferenceArr[0];
                  const preference = await preferencesAPI.get(
                    preferenceId,
                    "PREFERENCE_GENERIC",
                    manageAccountState.id
                  );

                  let updatedPreferences = deepClone(preference.preferences);
                  const newEmailDigest = {
                    days: { 1: true, 2: false, 3: false, 4: false, 5: false },
                    period: 1,
                  };
                  updatedPreferences.emailDigest = newEmailDigest;

                  const objToSave = {
                    preferences: updatedPreferences,
                  };
                  const savePreferenceResponde = await preferencesAPI.save(
                    objToSave,
                    "PREFERENCE_GENERIC",
                    deepClone(preference)
                  );
                  if (
                    !savePreferenceResponde ||
                    savePreferenceResponde?.status !== "OK"
                  ) {
                    throw new Error(
                      "Something went wrong on update preferences"
                    );
                  }
                }
              }
            }
            //#endregion

            const response = await userAPI.edit(_manageAccountState);
            if (response.status === "OK") {
              const subscriptions = retrieveSubscriptionsForSaving();
              await subscribe(subscriptions.toSubscribe, manageAccountState.id);
              //! unsubscribe is not available yet
              //unsubscribe(subscriptions.toUnsubscribe)
              //! --------------------------------
              feedback.show({
                type: "success",
                message: `Account [${response.data.user.firstName} ${response.data.user.lastName}] has been updated successfully!`,
              });
              closeModal();
              resetFilter();
              fetchDataWithDefaultFilter();
            } else {
              feedback.show({
                type: "error",
                message: `Something went wrong on update Account[${response.data.user.firstaName} ${response.data.user.lastName}]`,
              });
            }
          } catch (error: any) {
            feedback.show({
              type: "error",
              message: error.message,
            });

            console.log(error);
          }
        } else {
          //CREATE
          let _manageAccountState = prepareDailyReportProp(manageAccountState);
          try {
            const response = await userAPI.save(_manageAccountState);

            if (response.status === "OK") {
              const subscriptions = retrieveSubscriptionsForSaving();
              try {
                await subscribe(
                  subscriptions.toSubscribe,
                  response.data.user.id
                );
              } catch (error) {
                feedback.show({
                  type: "warning",
                  message: `Account [${response.data.user.firstName} ${response.data.user.lastName}] has been created successfully but something went wrong with the subscriptions!`,
                });
              }
              //! unsubscribe is not available yet
              //unsubscribe(subscriptions.toUnsubscribe)
              //! --------------------------------
              feedback.show({
                type: "success",
                message: `Account [${response.data.user.firstName} ${response.data.user.lastName}] has been created successfully!`,
              });
              closeModal();
              resetFilter();
              fetchDataWithDefaultFilter();
              //TODO: eliminare le key: productCode, subscriptions;
            } else {
              feedback.show({
                type: "error",
                message: `Something went wrong on create Account[${response.data.user.firstaName} ${response.data.user.lastName}]!`,
              });
            }
          } catch (error: any) {
            feedback.show({
              type: "error",
              message: error.message,
            });

            console.log(error);
          }
        }
      }
    } catch (error) {
      throw new Error(error as any);
    }
  }, [
    closeModal,
    feedback,
    fetchDataWithDefaultFilter,
    initStateForContentModal,
    preferencesAPI,
    resetFilter,
    retrieveSubscriptionsForSaving,
    subscribe,
    userAPI,
  ]);

  const handleClickManage = useCallback(() => {
    onManage(accountToManage);
  }, [onManage, accountToManage]);

  const tableOptions: Omit<TableParamsV2, "node"> = useMemo(
    () => ({
      tableOption: {
        columns,
        showRowIndex: true,
        selectableRows: true,
        addRowCheckbox: true,
      },
      events: {
        // rowClick: onRowSelect,
        onTableReady: () => setTableReady(true),
        onRowDbClick: (e, row) => {
          selectOnDBClick(e, row);
          setTimeout(() => {
            hiddenButtonRef.current?.click();
          }, 50);
        },

        rowSelectionChanged: (
          data: any,
          rows: RowComponent[],
          selected: RowComponent[],
          deselected: RowComponent[]
        ) => {
          const _rows: any[] = [];
          if (rows.length > 0) {
            for (const r of rows) {
              const rowData = r.getData();
              _rows.push(rowData);
            }
            setSelectedRows(_rows);
          } else {
            setSelectedRows(_rows);
          }
        },
      },
    }),
    [columns, selectOnDBClick]
  );

  return (
    <Grid
      container
      position={"relative"}
      justifyContent={"center"}
      py={1}
      px={2}
      gap={2}
      height={"100%"}
      overflow={"hidden"}
      minHeight={0}
      flexDirection={"column"}
      flexWrap={"nowrap"}
    >
      <Box height={"auto"} flexBasis={"auto"}>
        <Box pt={1} display={"flex"} flexDirection={"column"} gap={1}>
          <AccountFilter
            filter={filter}
            ref={acocuntFilterRef}
            onSearch={onSearch}
            typeData={userTypes}
          />

          <Box display={"flex"} gap={1} justifyContent={"space-between"} pt={1}>
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Button
                size="small"
                disabled={selectedRows.length > 1 || selectedRows.length < 1}
                onClick={handleClickManage}
              >
                Manage
              </Button>
              <Button
                size="small"
                sx={{ width: 0, height: 0, display: "none" }}
                onClick={handleClickManage}
                ref={hiddenButtonRef}
              ></Button>
              <Button
                size="small"
                disabled={selectedRows.length > 1 || selectedRows.length < 1}
                onClick={onImpersonate}
              >
                Impersonate
              </Button>

              <Button
                size="small"
                disabled={selectedRows.length === 0}
                onClick={goToAnalytics}
              >
                {selectedRows.length === 0 || selectedRows.length === 1
                  ? "Analyse usage"
                  : `Analyse usage of ${selectedRows.length} accounts`}
              </Button>

              <Typography>
                <strong>Total: {coutRows}</strong>
              </Typography>
            </Box>

            <Button
              endIcon={<PersonAddIcon />}
              size="small"
              color="secondary"
              onClick={onCreateNewAccount}
            >
              Create new account
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid
        height={"100%"}
        position={"relative"}
        width={"100%"}
        overflow={"hidden"}
        item
        xs={12}
      >
        {/* {isTableDataLoading && (
          <Box>
            <CircularProgress color="secondary" />
            <Typography>Loading users data...</Typography>
          </Box>
        )} */}
        <Box
          display={isTableDataLoading ? "none" : "flex"}
          minHeight={0}
          height={"100%"}
          width={"100%"}
        >
          <ReusableModal
            maxWidth="90%"
            width="70%"
            children={
              <ManageAccountModalContent
                initState={initStateForContentModal}
                ref={manageAccountModalContentRef}
                backup={selectedRows[0] ?? null}
                sellers={sellers}
              />
            }
            modalTitle={
              accountToManage?.id != null ? (
                <Typography>
                  Manage{" "}
                  <strong>{`${accountToManage?.firstName ?? ""} ${
                    accountToManage?.lastName ?? ""
                  }`}</strong>{" "}
                  {accountToManage?.group ? `(${accountToManage?.group})` : ""}
                </Typography>
              ) : (
                <Typography>Create new account</Typography>
              )
            }
            isOpen={showManageAccountModal}
            setIsOpen={closeModal}
            buttons={[
              <Button color="secondary" onClick={onSaveAccountChanges}>
                Save
              </Button>,
            ]}
          />

          {/* <Tabulator
            ref={tabulatorRef}
            onSelectRows={onRowSelect}
            selectableRows={1}
            data={tableData}
            columns={columns}
            customFooter={`<div> <strong>Total: ${tableData.length}</strong></div>`}
          /> */}

          <TrendratingTableV2 ref={tableRef} config={tableOptions} />
        </Box>
        <Box display={isTableDataLoading ? "block" : "none"}>
          <TableDataLoader />
        </Box>
      </Grid>
    </Grid>
  );
});

export default Accounts;
const TableDataLoader = () => {
  return (
    <Box
      gap={2}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
      flexDirection={"column"}
    >
      <CircularProgress color="secondary" />
      <Typography>Loading users data...</Typography>
    </Box>
  );
};
